import { lazy } from 'react';
import { Box, Text, Group, useMantineTheme, ThemeIcon, RingProgress } from '@mantine/core';
import { IconChartPie, IconTrendingUp, IconTrendingDown } from '@tabler/icons-react';
import { WidgetProps } from '../../types';
import { registerWidget } from '../../registry';
import { useVariantTextSizes } from '../../hooks/useWidgetPreferences';
import { usePreferencesStore } from '../../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../../hooks/useAccessibilityStyles';

// ============================================================================
// CompletionRateStat Widget Component - Headerless individual stat
// ============================================================================

function CompletionRateStatWidget({ globalData, instanceId, settings }: WidgetProps) {
  const theme = useMantineTheme();
  // Get text sizes scaled for the current size variant
  const { headingSize, smallTextSize, iconSize } = useVariantTextSizes(settings?.sizeVariant);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const stats = globalData?.taskStats;
  const completedTasks = stats?.done || 0;
  const totalTasks = stats?.total || 0;

  // Calculate completion rate
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Mock trend data
  const trend = 4.8;
  const trendUp = trend > 0;

  // Color based on completion rate
  const getRateColor = () => {
    if (completionRate >= 80) return 'green';
    if (completionRate >= 50) return 'yellow';
    return 'red';
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        textAlign: 'center',
      }}
    >
      <RingProgress
        size={Math.round(iconSize * 3.5)}
        thickness={Math.max(4, Math.round(iconSize / 3))}
        roundCaps
        sections={[{ value: completionRate, color: getRateColor() }]}
        label={
          <Text size={smallTextSize} ta="center" fw={700} style={accessibilityStyles}>
            {completionRate}%
          </Text>
        }
      />
      <Text size={smallTextSize} c="dimmed" mt={4} mb={4} style={accessibilityStyles}>
        Completion Rate
      </Text>
      <Group gap={4} justify="center">
        {trendUp ? (
          <IconTrendingUp size={Math.max(10, iconSize - 6)} color={theme.colors.green[6]} />
        ) : (
          <IconTrendingDown size={Math.max(10, iconSize - 6)} color={theme.colors.red[6]} />
        )}
        <Text size={smallTextSize} c={trendUp ? 'green' : 'red'} fw={500} style={accessibilityStyles}>
          {trendUp ? '+' : ''}{trend}%
        </Text>
      </Group>
    </Box>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'completion-rate-stat',
  name: 'Completion Rate',
  description: 'Single metric showing task completion percentage with trend',
  icon: IconChartPie,
  category: 'stats',
  component: lazy(() => Promise.resolve({ default: CompletionRateStatWidget })),
  sizes: {
    base: { cols: 6, rows: 2 },
    sm: { cols: 4, rows: 2 },
    md: { cols: 3, rows: 2 },
    lg: { cols: 2, rows: 2 },
    xl: { cols: 2, rows: 2 },
    tv: { cols: 2, rows: 2 },
  },
  sizeVariants: {
    variants: ['tiny', 'small', 'medium', 'large'],
    defaultVariant: 'small',
    variantSizes: {
      tiny: {
        base: { cols: 4, rows: 1 },
        sm: { cols: 3, rows: 1 },
        md: { cols: 2, rows: 1 },
        lg: { cols: 2, rows: 1 },
        xl: { cols: 2, rows: 1 },
        tv: { cols: 2, rows: 1 },
      },
      small: {
        base: { cols: 6, rows: 2 },
        sm: { cols: 4, rows: 2 },
        md: { cols: 3, rows: 2 },
        lg: { cols: 2, rows: 2 },
        xl: { cols: 2, rows: 2 },
        tv: { cols: 2, rows: 2 },
      },
      medium: {
        base: { cols: 6, rows: 2 },
        sm: { cols: 6, rows: 2 },
        md: { cols: 4, rows: 2 },
        lg: { cols: 3, rows: 2 },
        xl: { cols: 3, rows: 2 },
        tv: { cols: 3, rows: 2 },
      },
      large: {
        base: { cols: 6, rows: 3 },
        sm: { cols: 6, rows: 3 },
        md: { cols: 4, rows: 3 },
        lg: { cols: 3, rows: 3 },
        xl: { cols: 3, rows: 3 },
        tv: { cols: 3, rows: 3 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['taskStats'],
  },
  hasHeader: false,
  displayStyle: 'stat',
  hasSettings: true,
  collapsible: false,
  expandable: false,
});

export default CompletionRateStatWidget;
